// TOEFL iBT Mock 7 — Reading (2026 format, two timed modules)
//
// Task types:
//   words    – Complete the Words: [[word]] marks a word whose second
//              half is hidden; the test taker types the missing letters.
//   daily    – Read in Daily Life: short everyday texts (notices, emails, memos).
//   academic – Read an Academic Passage: short passage, 5 questions
//              (the last question is a "where does this sentence fit?" item;
//              the [A]–[D] markers are part of the passage text).
//
// Everyone takes module1. Scoring >= 60% on it routes to the harder
// "upper" Module 2; otherwise the easier "lower" Module 2.
//
// NOTE: Mock Test 7 in the PDF has a single Module 2, so `upper` and `lower`
// currently point to the same content. Replace `lower` with easier material
// if you want true adaptive routing.

// Each module is timed separately (about 30 minutes in total).
export const MODULE_SECONDS = { module1: 16 * 60, module2: 14 * 60 };

export const TASK_LABELS = {
  words: "Complete the Words",
  daily: "Read in Daily Life",
  academic: "Academic Passage",
};

// ── Module 1 ─────────────────────────────────────────────────
const module1 = [
    {
      id: "m1-words",
      type: "words",
      title: "Phytoplankton",
      text: "Phytoplankton are microscopic organisms that [[inhabit]] the upper sunlit [[layers]] of the ocean. Like terrestrial [[plants]], they contain chlorophyll and [[require]] sunlight to live and grow. They [[form]] the foundation of most marine food webs, [[sustain]] diverse species from tiny fish to massive [[whales]]. Furthermore, these organisms play a [[crucial]] role in the global carbon cycle by [[absorbing]] carbon dioxide during photosynthesis. A sudden [[decline]] in their populations could have [[severe]] [[consequences]] for both ocean health and global climate [[stability]].",
    },
    {
      id: "m1-daily1",
      type: "daily",
      title: "Makerspace Welcome Email",
      text: `Subject: Welcome to the Midtown Makerspace Community!

Dear Ms. Lin,

Welcome to the Midtown Makerspace! We are thrilled that you have decided to join our local hub of creators, hobbyists, and do-it-yourself enthusiasts. Whether you are working on a simple woodworking project or prototyping new electronics, our facility is equipped to support your creative journey.

As an active member, you can now reserve workbenches up to two weeks in advance using our mobile app. You also qualify for free enrollment in our weekly introductory safety classes and can borrow hand tools to take home for up to three days. Additionally, our digital newsletter will keep you updated on upcoming guest demonstrations and community build nights.

Before you can begin reserving equipment, there is one final step. Please log in to your member portal to read and electronically sign our standard liability waiver. You will also need to complete a brief, ten-question safety quiz regarding the proper use of our shared power tools. Once these items are submitted, your digital entry keycard will be activated within 24 hours.

We look forward to seeing what you create. If you encounter any issues logging in, please reach out to our support team.

Best regards,

Midtown Makerspace Administration`,
      questions: [
        {
          question: "According to the email, what is one privilege members have?",
          options: [
            "Buying electronics at a lower price",
            "Reserving power tools a month in advance",
            "Teaching weekly introductory safety classes",
            "Borrowing hand tools to use at home",
          ],
          correct: 3,
        },
        {
          question: "Which of the following is NOT mentioned as a feature or benefit of the Midtown Makerspace?",
          options: [
            "A newsletter about upcoming events",
            "Discounts on construction materials",
            "A mobile application for reservations",
            "Workbenches for project development",
          ],
          correct: 1,
        },
        {
          question: "What must Ms. Lin do to finalize her registration and activate her keycard?",
          options: [
            "Reply to the email with her project ideas",
            "Pick up a physical keycard from the support team",
            "Sign a legal document and take a short test",
            "Attend a community build night to meet the administration",
          ],
          correct: 2,
        },
      ],
    },
    {
      id: "m1-daily2",
      type: "daily",
      title: "Flu Vaccine Clinic Hours",
      text: `Northgate Family Clinic: Flu Vaccine Clinic Hours

Beginning Monday, October 19, Northgate Family Clinic will offer walk-in flu vaccines every weekday from 8:00 a.m. to 11:00 a.m. in the Wellness Annex, next to the main parking lot. No appointment is needed, but patients should bring their insurance card. Most plans cover the vaccine fully. Patients without insurance can pay a $35 fee at the front desk.

Children under 12 must be accompanied by a parent or guardian. Anyone who feels feverish on the day of the visit should call the nurse line at 555-0142 and reschedule rather than come in. The Annex will close at 11:00 a.m. sharp so staff can prepare for afternoon appointments.`,
      questions: [
        {
          question: "What is the main purpose of the notice?",
          options: [
            "To ask patients to schedule appointments for the flu",
            "To announce walk-in hours for flu vaccines",
            "To explain how insurance plans are chosen",
            "To introduce a new parking lot for patients",
          ],
          correct: 1,
        },
        {
          question: "How much must a patient without insurance pay?",
          options: [
            "$11",
            "$42",
            "Nothing, because the vaccine is free for everyone",
            "$35",
          ],
          correct: 3,
        },
        {
          question: "What should a patient with a fever do on the day of the visit?",
          options: [
            "Wait in the Annex until 11:00 a.m.",
            "Bring a parent or guardian",
            "Call the nurse line and reschedule",
            "Pay the fee at the front desk",
          ],
          correct: 2,
        },
      ],
    },
    {
      id: "m1-academic",
      type: "academic",
      title: "How Glaciers Shape Landscapes",
      text: `Glaciers are large, slowly moving masses of ice that form where more snow falls each year than melts. Over many decades, the weight of accumulated snow compresses the lower layers into dense ice, and once this ice becomes thick enough, gravity causes it to flow downhill. Although the movement is usually only a few centimeters to a few meters per day, glaciers are among the most powerful agents of erosion on Earth.

Glaciers reshape land in two main ways. First, as ice flows over bedrock, it plucks loose fragments of rock from the surface and carries them along. [A] These embedded fragments then act like sandpaper, grinding and scratching the rock beneath the glacier in a process called abrasion. Over thousands of years, the combined effect carves deep valleys with steep walls and broad, rounded floors, giving glaciated mountain valleys their characteristic U shape, in contrast to the narrower V shape cut by rivers.

The debris that a glacier carries is eventually deposited when the ice melts. [B] Some of this material builds up along the glacier's sides and front as ridges known as moraines, which mark the farthest advance of the ice long after it has retreated. Larger boulders, called erratics, may be carried for hundreds of kilometers and left in places where the surrounding rock is entirely different. [C] Geologists use such stranded boulders to reconstruct the routes of ancient ice sheets.

Glacial landscapes also matter to human activity. Meltwater from mountain glaciers supplies drinking water and irrigation to millions of people, and the lakes left behind by retreating ice, such as the Great Lakes of North America, remain economically important. [D] However, as global temperatures rise, many glaciers are shrinking rapidly, raising concerns about future water supplies and sea levels.`,
      questions: [
        {
          question: "The word \"plucks\" in the passage is closest in meaning to",
          options: [
            "melts",
            "pulls away",
            "polishes",
            "measures",
          ],
          correct: 1,
        },
        {
          question: "According to paragraph 2, what causes abrasion?",
          options: [
            "Rivers cutting through the valley floor",
            "Meltwater freezing inside cracks in the rock",
            "Rock fragments carried in the ice scraping against the bedrock",
            "The pressure of fresh snow falling on the glacier",
          ],
          correct: 2,
        },
        {
          question: "What can be inferred from paragraph 3 about erratics?",
          options: [
            "They are usually made of the same rock as the land around them.",
            "Their presence can show that ice once covered an area.",
            "They are formed when glaciers melt very quickly.",
            "They are rare in regions that were once glaciated.",
          ],
          correct: 1,
        },
        {
          question: "Why does the author mention the Great Lakes in paragraph 4?",
          options: [
            "To argue that the Great Lakes are shrinking",
            "To compare U-shaped and V-shaped valleys",
            "To explain how moraines are formed",
            "To show that glacial landforms can have lasting economic value",
          ],
          correct: 3,
        },
        {
          question: "Where would the following sentence best fit? \"Some of these fragments are as small as grains of sand, while others are as large as houses.\"",
          options: [
            "[A]",
            "[B]",
            "[C]",
            "[D]",
          ],
          correct: 0,
        },
      ],
    },
];

// ── Module 2 ─────────────────────────────────────────────────
const module2 = [
    {
      id: "m2-words",
      type: "words",
      title: "Sleep and Memory",
      text: "Sleep plays an important role in how the human brain stores new information. [[During]] the [[day]], people [[take]] in a huge [[amount]] of experience, [[but]] much of it is [[fragile]] and [[easily]] forgotten. [[While]] we sleep, [[the]] brain [[replays]] recent [[events]] and [[strengthens]] the [[connections]] that [[matter]] most. [[This]] process, [[known]] as memory [[consolidation]], helps transform short term impressions into stable long term knowledge. Studies show that students who sleep well after studying often perform better on tests than those who stay awake all night. Therefore, regular and sufficient rest should be seen as a necessary part of effective learning rather than a waste of valuable time.",
    },
    {
      id: "m2-daily1",
      type: "daily",
      title: "Flight Update to Lisbon",
      text: `Subject: Update on your flight to Lisbon (TA 482)

Dear Mr. Okafor,

Your flight TA 482 on Thursday, November 5, will now depart at 9:40 p.m. instead of 6:15 p.m. because of a delay in the arrival of the aircraft from Madrid. Your arrival in Lisbon is expected at 12:05 a.m. on Friday.

You do not need to change anything. Your seat, 23C, and your checked baggage allowance remain the same. Because of the long wait, we have added a meal voucher worth 15 euros to your booking; you can use it at any restaurant in Terminal 2. If the new departure time does not suit you, you may request a full refund or a different flight through the Manage Booking page before 6:00 p.m. on November 4.

We apologize for the inconvenience.

Tandem Air Customer Care`,
      questions: [
        {
          question: "Why was the email written?",
          options: [
            "To confirm a change of seat",
            "To advertise restaurants in Terminal 2",
            "To announce a new route to Madrid",
            "To inform a passenger of a delayed departure",
          ],
          correct: 3,
        },
        {
          question: "What has the airline added to Mr. Okafor's booking?",
          options: [
            "A free seat upgrade",
            "An extra checked bag",
            "A meal voucher",
            "A hotel room in Lisbon",
          ],
          correct: 2,
        },
        {
          question: "What should Mr. Okafor do if he cannot travel at the new time?",
          options: [
            "Call the airline after the flight departs",
            "Go to Madrid and wait for the aircraft",
            "Choose a new seat at the airport",
            "Request a refund or another flight before 6:00 p.m. on November 4",
          ],
          correct: 3,
        },
      ],
    },
    {
      id: "m2-daily2",
      type: "daily",
      title: "Pottery Class Chat",
      text: `Community Center Chat: Pottery Class

Dana: Did you see the email? Thursday's pottery class is moved to the back studio.

Luis: Yes, the front room is being used for the blood drive. Is it still at 6:30?

Dana: Yes, but Ms. Ferreira said we should arrive ten minutes early because the kiln needs to be unloaded first.

Luis: Okay. Do we still need to bring our own aprons?

Dana: Apparently the center bought some, so you can borrow one at the door. Clay is included in the $60 fee, but glazes are extra this week.

Luis: Good to know. I'll bring cash for the glazes.

Dana: Better to check first. I think they only take cards at the desk, and the studio has no card reader.`,
      questions: [
        {
          question: "Why has Thursday's class been moved?",
          options: [
            "The kiln needs repairs",
            "Ms. Ferreira is away",
            "The class has become too large",
            "The front room will be used for another event",
          ],
          correct: 3,
        },
        {
          question: "What do the speakers say about aprons?",
          options: [
            "Students can borrow them from the center",
            "They must be purchased for $60",
            "They are not allowed in the studio",
            "They are sold at the blood drive",
          ],
          correct: 0,
        },
        {
          question: "What can be inferred about paying for glazes?",
          options: [
            "Luis may not be able to pay with cash",
            "Glazes are included in the class fee",
            "Glazes can be paid for only in the studio",
            "Luis has already paid for them",
          ],
          correct: 0,
        },
      ],
    },
    {
      id: "m2-academic",
      type: "academic",
      title: "The Great Vowel Shift and the Making of English Spelling",
      text: `Languages are never static. Over successive generations, pronunciation, vocabulary, and grammar gradually shift, so that speakers of a language today would often struggle to follow the speech of their distant ancestors. Linguists regard such change as an inevitable consequence of language being passed from one generation to the next, not as a sign of decay or carelessness.

One of the most thoroughly studied examples is the Great Vowel Shift, which transformed English between roughly the fourteenth and eighteenth centuries. [A] During this period, the long vowels of Middle English were pronounced with the tongue in progressively higher positions, and the highest vowels became diphthongs. [B] The word "house", for instance, was once pronounced something like "hoos". The causes of the shift remain debated, with some scholars pointing to social mixing after the Black Death and others to the imitation of prestigious speakers.

The shift left a lasting mark on written English. Printing reached England in the 1470s, when William Caxton established his press, and spelling conventions began to settle just as pronunciation was in motion. [C] Many words were therefore fixed in print with spellings that reflected the older sounds. [D] Because of this, English vowel letters often represent sounds quite different from those they stand for in Spanish or Italian, two languages whose spelling systems were standardized under more stable conditions.

Language change is not confined to the past. New words enter through borrowing, technology, and playful coinage, while grammatical habits shift more slowly. Contemporary linguists use large digital collections of texts and recordings to trace such developments as they happen, and they generally find that innovations begin among younger speakers before spreading through the wider community. Critics who lament these changes as a decline in standards are therefore resisting a process that has operated throughout the history of every living language.`,
      questions: [
        {
          question: "The word \"inevitable\" in the passage is closest in meaning to",
          options: [
            "harmful",
            "unavoidable",
            "gradual",
            "intentional",
          ],
          correct: 1,
        },
        {
          question: "According to paragraph 2, what happened to the long vowels of Middle English during the Great Vowel Shift?",
          options: [
            "They disappeared from most English words.",
            "They were replaced by vowels borrowed from Spanish.",
            "They were pronounced with the tongue in higher positions.",
            "They became shorter in order to match the spelling.",
          ],
          correct: 2,
        },
        {
          question: "What can be inferred from paragraph 3 about English spelling?",
          options: [
            "It does not always correspond to how words are currently pronounced.",
            "It was invented only after the Great Vowel Shift had ended.",
            "It is easier for learners than the spelling of Spanish or Italian.",
            "It was deliberately designed to preserve rare vowel sounds.",
          ],
          correct: 0,
        },
        {
          question: "Why does the author mention Spanish and Italian in paragraph 3?",
          options: [
            "To argue that English borrowed most of its vowels from these languages",
            "To explain why Caxton chose to open a printing press in England",
            "To suggest that these languages avoided the changes affecting English",
            "To show that English vowel letters have unusual values compared with languages whose spelling is more stable",
          ],
          correct: 3,
        },
        {
          question: "Where would the following sentence best fit? \"Words such as \"knight\" still contain letters that were once pronounced but have long since fallen silent.\"",
          options: [
            "[A]",
            "[B]",
            "[C]",
            "[D]",
          ],
          correct: 3,
        },
      ],
    },
];

export const readingModules = {
  module1,
  upper: module2,
  lower: module2,
};

// Splits "[[word]]" markers into { shown, missing } segments.
// The first half (rounded up) of each word is shown; the rest is typed.
export function parseWordsText(text) {
  const parts = [];
  const re = /\[\[([^\]]+)\]\]/g;
  let last = 0;
  let match;
  let blank = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) parts.push({ kind: "text", value: text.slice(last, match.index) });
    const word = match[1];
    const keep = Math.ceil(word.length / 2);
    parts.push({ kind: "blank", index: blank++, word, shown: word.slice(0, keep), missing: word.slice(keep) });
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push({ kind: "text", value: text.slice(last) });
  return parts;
}